import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DataDisplayTable } from 'components/data-display';
import { ShowHide } from 'components/layout';
import { DefectStatus } from 'pages/defects/shared';
import { DefectHelperFunctions } from 'helpers';

const emptyValue = "-";
const dateDisplayFormat = "DD/MM/YYYY";
const reportNotAvailableText = "Report not available, you do not have permission to this asset's location for inspections";
const tyreCheckSource = "Tyre Check";
const loadingRows = [
    { key: "vehicle", header: "Vehicle", value: emptyValue },
    { key: "type", header: "Type", value: emptyValue },
    { key: "defect", header: "Defect", value: emptyValue },
    { key: "defect-date", header: "Defect Date", value: emptyValue }
];

function formatDate(value) {
    return value ? moment(value).format(dateDisplayFormat) : emptyValue;
}

function formatVehicle(defect) {
    return defect.fleetNumber ? `${defect.assetRegistration} / ${defect.fleetNumber}` : defect.assetRegistration;
}

function statusRow(defect) {
    return row("status", "Status", <DefectStatus status={defect.defectStatus} />);
}

function formatYesNo(value) {
    return value ? "Yes" : "No";
}

function formatPart(part) {
    if (part == null) {
        return null;
    }
    if (part.text && part.code) {
        return `${part.text} (${part.code})`;
    }
    return part.text ?? part.code;
}

function formatMarkingCode(repairCode) {
    if (repairCode == null) {
        return null;
    }
    return repairCode.text ? `${repairCode.code} - ${repairCode.text}` : repairCode.code;
}

function row(key, header, value) {
    return { key, header, value };
}

// Rows whose value is absent are left out rather than shown empty.
function presentRows(rows) {
    return rows.filter((x) => x.value != null && x.value !== "");
}

function Section({ heading, rows }) {
    return (
        <div className="spacer-bottom">
            <h3>{heading}</h3>
            <DataDisplayTable data={rows} />
        </div>
    );
}

Section.propTypes = {
    heading: PropTypes.string.isRequired,
    rows: PropTypes.array.isRequired
};

function signoffRows(defect) {
    if (defect.rectified === true) {
        return [
            row("maintenance-provider", "Maintenance Provider", defect.maintenanceProvider ?? emptyValue),
            row("rectified-by", "Rectified By", defect.rectifiedBy ?? emptyValue),
            row("rectified-date", "Rectified Date", formatDate(defect.rectifiedDate)),
            row("action-taken", "Action Taken", defect.rectifiedComment ?? emptyValue),
            row("signed-off-by", "Signed Off By", defect.signoffUser ?? emptyValue),
            row("signoff-date", "Signoff Date", formatDate(defect.signoffDate))
        ];
    }

    return [
        row("reason", "Reason", defect.rectifiedComment ?? emptyValue),
        row("signed-off-by", "Signed Off By", defect.signoffUser ?? emptyValue),
        row("signoff-date", "Signoff Date", formatDate(defect.signoffDate))
    ];
}

function raisedOn(defect) {
    switch (defect.defectTypeKey) {
        case DefectHelperFunctions.defectTypeKeys.event:
            return defect.eventName ?? emptyValue;
        case DefectHelperFunctions.defectTypeKeys.check:
            return defect.checkName ?? emptyValue;
        case DefectHelperFunctions.defectTypeKeys.tyreCheck:
            return tyreCheckSource;
        default:
            return emptyValue;
    }
}

function sourceDefectRows(defect) {
    const rows = [
        row("vehicle", "Vehicle", formatVehicle(defect)),
        row("type", "Type", defect.defectType),
        row("defect", "Defect", defect.defectName),
        row("raised-on", "Raised On", raisedOn(defect)),
        row("defect-date", "Defect Date", formatDate(defect.defectDate)),
        statusRow(defect),
        row("defect-comment", "Defect Comment", defect.defectComment || emptyValue)
    ];

    if (defect.signoffDate != null) {
        rows.push(...signoffRows(defect));
    }

    return rows;
}

function inspectionRows(defect, canOpenInspectionReport) {
    const reportUrl = canOpenInspectionReport ? DefectHelperFunctions.getInspectionDefectUrl(defect) : null;
    let report = reportNotAvailableText;
    if (reportUrl) {
        report = <a href={reportUrl} id="defect-details-view-report">View report</a>;
    }

    return [
        row("vehicle", "Vehicle", formatVehicle(defect)),
        row("type", "Type", defect.defectType),
        row("product", "Product", defect.productName ?? emptyValue),
        row("inspection-date", "Inspection Date", formatDate(defect.defectDate)),
        row("report", "Report", report),
        statusRow(defect),
        row("inspector-comment", "Inspector Comment", defect.defectComment || emptyValue)
    ];
}

function classificationRows(classification) {
    return presentRows([
        row("location", "Location", formatPart(classification?.location)),
        row("component", "Component", formatPart(classification?.component)),
        row("prefix", "Prefix", formatPart(classification?.prefix)),
        row("classification-defect", "Defect", formatPart(classification?.defect)),
        row("marking-code", "Marking Code", formatMarkingCode(classification?.repairCode)),
        row("tm-number", "TM Number", classification?.tmNumber),
        row("ocrs-points", "OCRS Points", classification?.ocrsPoints),
        row("ocrs-s-marked", "OCRS S-Marked", classification?.ocrsSMarked == null ? null : formatYesNo(classification.ocrsSMarked)),
        row("safety-related", "Safety Related", classification?.safety == null ? null : formatYesNo(classification.safety))
    ]);
}

function driverReportRows(classification) {
    return presentRows([
        row("found-by-driver", "Found By Driver", classification?.foundByDriver == null ? null : formatYesNo(classification.foundByDriver)),
        row("reported-by-driver", "Reported By Driver", classification?.reportedByDriver == null ? null : formatYesNo(classification.reportedByDriver)),
        row("reported-to", "Reported To", classification?.reportedToWho)
    ]);
}

export function DefectDetailsContent(
    {
        defect = null,
        loading = false,
        canOpenInspectionReport = false
    }
) {
    if (loading || defect == null) {
        return <DataDisplayTable data={loadingRows} loading />;
    }

    const hasSignoff = defect.signoffDate != null;

    if (DefectHelperFunctions.isInspectionDefect(defect)) {
        const driverReport = driverReportRows(defect.classification);

        return (
            <div className="defect-details-content">
                <Section heading="Defect" rows={inspectionRows(defect, canOpenInspectionReport)} />
                <Section heading="Classification" rows={classificationRows(defect.classification)} />
                <ShowHide
                    evaluator={driverReport.length > 0}
                    show={<Section heading="Driver Report" rows={driverReport} />}
                />
                <ShowHide
                    evaluator={hasSignoff}
                    show={<Section heading="Sign-off" rows={signoffRows(defect)} />}
                />
            </div>
        );
    }

    return (
        <div className="defect-details-content">
            <DataDisplayTable data={sourceDefectRows(defect)} />
        </div>
    );
}

DefectDetailsContent.propTypes = {
    defect: PropTypes.object,
    loading: PropTypes.bool,
    canOpenInspectionReport: PropTypes.bool
};
